import React, { useState, useCallback, useEffect } from 'react'
import { createAICommand, AICommandType, AICommandStatus, createNodeCommandPayload, isCommandComplete } from '~/utils/aiCommandUtils'

interface QuickStartNodeData {
  challenge?: string
  desiredOutcome?: string
  statusMessage?: string
  configuration?: NodeConfiguration
  label?: string
  isMinimized?: boolean
  isLocked?: boolean
  assignedEmails?: string
  dataSettings?: NodeDataSettings
  linkedKnowledge?: string
  lastModified?: number
  documentRequest?: {
    document: string
    'sysagent-cmd': string
    'sysagent-response': string
    [key: string]: any
  }
  // Added for QuickStartNode
  levelOfUsage?: 'Guided' | 'Open' | 'TotalFreedom'
  template?: string
  style?: string
  input?: string
  aiModel?: string
  config?: Record<string, any>
}

interface NodeConfiguration {
  isAutomated: boolean
  modifierprompt: string
}

interface NodeDataSettings {
  inputLinks: any[]
  outputLinks: any[]
  availableNodes: any[]
  exposedData?: any
  autoSync?: boolean
}

const useQuickStartState = (id: string, initialData: QuickStartNodeData, updateNodeData: (id: string, data: any) => void) => {
  // Initialize with actual data, but use useEffect to keep in sync
  const [challenge, setChallenge] = useState(initialData.challenge || '')
  const [desiredOutcome, setDesiredOutcome] = useState(initialData.desiredOutcome || '')
  const [statusMessage, setStatusMessage] = useState(initialData.statusMessage || '')
  const [assignedEmails, setAssignedEmails] = useState(initialData.assignedEmails || '')
  const [isLocked, setIsLocked] = useState(initialData.isLocked || false)
  const [isMinimized, setIsMinimized] = useState(initialData.isMinimized || false)
  const [nodeConfig, setNodeConfig] = useState<NodeConfiguration>(
    initialData.configuration || { isAutomated: false, modifierprompt: '' }
  )
  const [isConfigDialogVisible, setIsConfigDialogVisible] = useState(false)
  const [showDebugInfo, setShowDebugInfo] = useState(false)

  // Track last synced data to avoid unnecessary updates
  const lastSyncedData = React.useRef<QuickStartNodeData>({
    challenge: initialData.challenge || '',
    desiredOutcome: initialData.desiredOutcome || '',
    statusMessage: initialData.statusMessage || '',
    configuration: initialData.configuration || { isAutomated: false, modifierprompt: '' },
    label: initialData.label || 'Quick Start',
    isMinimized: initialData.isMinimized || false,
    isLocked: initialData.isLocked || false,
    assignedEmails: initialData.assignedEmails || '',
    dataSettings: initialData.dataSettings || { inputLinks: [], outputLinks: [], availableNodes: [], autoSync: false },
    linkedKnowledge: initialData.linkedKnowledge || '',
    lastModified: initialData.lastModified || Date.now(),
    documentRequest: initialData.documentRequest || undefined,
  })

  const syncNodeData = useCallback((newData: Partial<QuickStartNodeData>) => {
    updateNodeData(id, (prevData) => {
      const cleanPrevData = { ...prevData }
      if ('data' in cleanPrevData && typeof cleanPrevData.data === 'object' && cleanPrevData.data !== null) {
        delete cleanPrevData.data // Prevent recursive nesting
      }
      const updatedData = { ...cleanPrevData, ...newData }
      lastSyncedData.current = updatedData // Update last synced state
      if (showDebugInfo) {
        console.log(`[QuickStartNode ${id}] syncNodeData:`, { prevData, newData, updatedData })
      }
      return updatedData
    })
  }, [id, updateNodeData, showDebugInfo])

  // Sync from collaborative state when it changes
  useEffect(() => {
    const hasExternalChange = (
      initialData.challenge !== lastSyncedData.current.challenge ||
      initialData.desiredOutcome !== lastSyncedData.current.desiredOutcome ||
      initialData.statusMessage !== lastSyncedData.current.statusMessage ||
      initialData.assignedEmails !== lastSyncedData.current.assignedEmails ||
      initialData.isMinimized !== lastSyncedData.current.isMinimized ||
      initialData.isLocked !== lastSyncedData.current.isLocked ||
      JSON.stringify(initialData.configuration) !== JSON.stringify(lastSyncedData.current.configuration)
    )
    if (hasExternalChange) {
      setChallenge(initialData.challenge || '')
      setDesiredOutcome(initialData.desiredOutcome || '')
      setStatusMessage(initialData.statusMessage || '')
      setAssignedEmails(initialData.assignedEmails || '')
      setIsMinimized(initialData.isMinimized || false)
      setIsLocked(initialData.isLocked || false)
      setNodeConfig(initialData.configuration || { isAutomated: false, modifierprompt: '' })
      lastSyncedData.current = { ...lastSyncedData.current, ...initialData }
      if (showDebugInfo) {
        console.log(`[QuickStartNode ${id}] External data change detected:`, initialData)
      }
    }
  }, [initialData, showDebugInfo, id])

  const handleProcessClick = useCallback(() => {
    if (!challenge.trim() || !desiredOutcome.trim()) {
      const errorMsg = 'Error: Both challenge and desired outcome are required.'
      syncNodeData({ statusMessage: errorMsg })
      return
    }

    const processingMsg = 'Processing triggered.'
    syncNodeData({ statusMessage: processingMsg })

    const documentRequest = createAICommand(
      AICommandType.QUICK_START,
      AICommandStatus.STARTED,
      createNodeCommandPayload(id, {
        challenge: challenge,
        desiredOutcome: desiredOutcome,
        document: 'sam-default',
      })
    )

    syncNodeData({ documentRequest })
  }, [challenge, desiredOutcome, syncNodeData])

  const handleLock = useCallback(() => {
    const newState = !isLocked
    syncNodeData({ isLocked: newState })
  }, [isLocked, syncNodeData])

  const handleMinimize = useCallback(() => {
    const newState = !isMinimized
    syncNodeData({ isMinimized: newState })
  }, [isMinimized, syncNodeData])

  const handleTitleChange = useCallback((newTitle: string) => {
    syncNodeData({ label: newTitle, lastModified: Date.now() })
  }, [syncNodeData])

  return {
    challenge,
    setChallenge,
    desiredOutcome,
    setDesiredOutcome,
    statusMessage,
    setStatusMessage,
    assignedEmails,
    setAssignedEmails,
    isLocked,
    setIsLocked,
    isMinimized,
    setIsMinimized,
    nodeConfig,
    setNodeConfig,
    isConfigDialogVisible,
    setIsConfigDialogVisible,
    showDebugInfo,
    setShowDebugInfo,
    syncNodeData,
    handleLock,
    handleMinimize,
    handleProcessClick,
    handleTitleChange,
  }
}

export default useQuickStartState